import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, ImageOff, Upload } from "lucide-react";

const API_BASE = `${window.location.origin}/api/banners`;

const BannerCard = ({ banner, busy, copied, onUpload, onCopy }) => {
  const inputRef = useRef(null);
  const url = banner.uploaded ? `${window.location.origin}${banner.path}` : null;

  return (
    <div
      data-testid={`banner-card-${banner.slot}`}
      className="rounded-3xl border border-linen bg-white p-6 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-lg font-semibold text-ink">{banner.slot}</h2>
          <p className="text-xs font-medium text-moss">
            {banner.expected_width} × {banner.expected_height} px
            {banner.uploaded && banner.width
              ? ` • enviado: ${banner.width}×${banner.height}px (${Math.round(banner.size_bytes / 1024)} KB)`
              : ""}
          </p>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${
            banner.uploaded ? "bg-mint text-forest" : "bg-sand text-moss"
          }`}
        >
          {banner.uploaded ? "No ar" : "Pendente"}
        </span>
      </div>

      <div className="mt-4 flex min-h-[72px] items-center justify-center overflow-auto rounded-2xl border border-dashed border-linen bg-sand/50 p-3">
        {banner.uploaded ? (
          <img
            src={`${url}?v=${banner.size_bytes}`}
            alt={`Banner ${banner.slot}`}
            data-testid={`banner-preview-${banner.slot}`}
            className="max-h-40 max-w-full object-contain"
          />
        ) : (
          <span className="flex items-center gap-2 text-xs text-moss">
            <ImageOff size={15} /> Nenhum arquivo enviado
          </span>
        )}
      </div>

      {banner.uploaded && (
        <div className="mt-4 flex items-center gap-2">
          <input
            readOnly
            value={url}
            data-testid={`banner-url-${banner.slot}`}
            onFocus={(e) => e.target.select()}
            className="w-full rounded-full border border-linen bg-cream px-3 py-2 font-mono text-[11px] text-forest"
          />
          <button
            onClick={() => onCopy(banner.slot, url)}
            data-testid={`banner-copy-${banner.slot}`}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-forest px-4 py-2 text-xs font-semibold text-cream transition-transform hover:-translate-y-0.5"
          >
            {copied === banner.slot ? <Check size={14} /> : <Copy size={14} />}
            {copied === banner.slot ? "Copiado!" : "Copiar URL"}
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
        className="hidden"
        data-testid={`banner-file-${banner.slot}`}
        onChange={(e) => {
          onUpload(banner.slot, e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy === banner.slot}
        data-testid={`banner-upload-${banner.slot}`}
        className="cta-shine mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-4 py-2.5 text-sm font-semibold text-cream transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      >
        <Upload size={15} />
        {busy === banner.slot ? "Enviando..." : banner.uploaded ? "Substituir imagem" : "Enviar imagem"}
      </button>
    </div>
  );
};

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const res = await fetch(API_BASE);
    const data = await res.json();
    setBanners(data.banners || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (slot, file) => {
    if (!file) return;
    setBusy(slot);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/${slot}/upload`, { method: "POST", body: fd });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Falha no upload");
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const copy = async (slot, url) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(slot);
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <div data-testid="admin-banners-page" className="min-h-screen bg-cream font-sans text-ink">
      <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-terracotta">Área administrativa</p>
        <h1 className="mt-2 font-serif text-3xl font-semibold sm:text-4xl">
          Banners de afiliados — <span className="italic text-terracotta">Monetizze</span>
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-moss">
          Envie ou substitua cada banner e copie a URL pública direta do arquivo para cadastrar na Monetizze.
          A URL de cada banner é estável: substituir a imagem não muda o endereço.
        </p>
        {error && (
          <p data-testid="admin-banners-error" className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </p>
        )}
        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {banners.map((b) => (
            <BannerCard key={b.slot} banner={b} busy={busy} copied={copied} onUpload={upload} onCopy={copy} />
          ))}
        </div>
      </div>
    </div>
  );
}
